import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

import Select from '../../../components/ui/Select';
import { Checkbox } from '../../../components/ui/Checkbox';
import CommunicationService from '../../../services/communications';
import aiSmartDialerService from '../../../services/openai';

const AISmartDialerPanel = () => {
  // Core states
  const [contacts, setContacts] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [selectedContacts, setSelectedContacts] = useState([]);
  const [aiGeneratedScript, setAiGeneratedScript] = useState('');
  const [campaigns, setCampaigns] = useState([]);
  const [callHistory, setCallHistory] = useState([]);
  
  // AI features states
  const [aiMode, setAiMode] = useState('smart'); // 'smart' or 'manual'
  const [prioritizedContacts, setPrioritizedContacts] = useState([]);
  const [realTimeCoaching, setRealTimeCoaching] = useState(null);
  const [activeCall, setActiveCall] = useState(null);
  const [conversationTranscript, setConversationTranscript] = useState('');
  const [aiInsights, setAiInsights] = useState(null);
  
  // UI states
  const [loading, setLoading] = useState(false);
  const [aiProcessing, setAiProcessing] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loadingData, setLoadingData] = useState(true);
  const [activeTab, setActiveTab] = useState('dialer'); // 'dialer', 'insights', 'campaigns'
  
  // AI Settings
  const [aiSettings, setAiSettings] = useState({
    callPurpose: 'lead_qualification',
    tone: 'professional',
    aggressiveness: 'medium',
    autoScript: true,
    realTimeCoaching: true,
    voicemailGeneration: true,
    postCallAnalysis: true
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoadingData(true);
    try {
      const [contactsRes, templatesRes, campaignsRes, callsRes] = await Promise.all([
        CommunicationService?.getContacts(),
        CommunicationService?.getTemplates('call'),
        CommunicationService?.getCampaigns(),
        CommunicationService?.getMessages({ type: 'call', limit: 20 })
      ]);

      if (contactsRes?.success) {
        setContacts(contactsRes?.data || []);
        // Auto-prioritize contacts with AI
        if (contactsRes?.data?.length > 0 && aiMode === 'smart') {
          prioritizeContactsWithAI(contactsRes?.data);
        }
      }
      
      if (templatesRes?.success) setTemplates(templatesRes?.data || []);
      if (campaignsRes?.success) setCampaigns(campaignsRes?.data?.filter(c => c?.type === 'call') || []);
      if (callsRes?.success) setCallHistory(callsRes?.data || []);
      
    } catch (error) {
      console.error('Error loading data:', error);
      setError('Failed to load data');
    } finally {
      setLoadingData(false);
    }
  };

  const prioritizeContactsWithAI = async (contactsList) => {
    setAiProcessing(true);
    try {
      const result = await aiSmartDialerService?.prioritizeContacts(contactsList);
      if (result?.success) {
        setPrioritizedContacts(result?.data?.prioritized_contacts || []);
        setAiInsights(result?.data?.insights);
      }
    } catch (error) {
      console.error('Error prioritizing contacts:', error);
    } finally {
      setAiProcessing(false);
    }
  };

  const generateAIScript = async (contactId = null, purpose = null) => {
    if (!contactId && selectedContacts?.length === 0) {
      setError('Please select at least one contact');
      return;
    }

    setAiProcessing(true);
    setError('');

    try {
      const targetContactId = contactId || selectedContacts?.[0];
      const contact = contacts?.find(c => c?.id === targetContactId);
      
      if (!contact) {
        setError('Contact not found');
        return;
      }

      const result = await aiSmartDialerService?.generateCallScript({
        contactInfo: {
          name: contact?.name,
          company: contact?.company,
          notes: contact?.notes || '',
          tags: contact?.tags || []
        },
        callPurpose: purpose || aiSettings?.callPurpose,
        tone: aiSettings?.tone
      });

      if (result?.success) {
        setAiGeneratedScript(result?.data?.opening + '\n\n' + result?.data?.main_pitch);
        setSuccess('AI script generated successfully!');
      } else {
        setAiGeneratedScript(result?.fallback?.opening + '\n\n' + result?.fallback?.main_pitch);
        setError('AI service unavailable, using fallback script');
      }
    } catch (error) {
      console.error('Error generating AI script:', error);
      setError('Failed to generate AI script');
    } finally {
      setAiProcessing(false);
    }
  };

  const initiateSmartCall = async (contactId) => {
    const contact = contacts?.find(c => c?.id === contactId);
    if (!contact?.phone) {
      setError(`${contact?.name || 'Contact'} has no phone number`);
      return;
    }

    setLoading(true);
    setError('');
    setSuccess('');

    try {
      // Generate AI script if auto-script is enabled
      if (aiSettings?.autoScript) {
        await generateAIScript(contactId);
      }

      // Create message record
      const messageRes = await CommunicationService?.createMessage({
        type: 'call',
        direction: 'outbound',
        contact_id: contactId,
        content: aiGeneratedScript || `AI Smart Call to ${contact?.name}`,
        to_address: contact?.phone,
        from_address: '+15559999999',
        status: 'pending',
        metadata: {
          ai_generated: true,
          call_purpose: aiSettings?.callPurpose,
          ai_mode: aiMode
        }
      });

      if (messageRes?.success) {
        // Set up active call tracking
        setActiveCall({
          messageId: messageRes?.data?.id,
          contactId,
          contactName: contact?.name,
          startTime: new Date(),
          transcript: ''
        });

        // Initiate the actual call
        const callRes = await CommunicationService?.makeCall({
          to: contact?.phone,
          message: aiGeneratedScript,
          record: true, // Always record for AI analysis
          messageId: messageRes?.data?.id
        });

        if (callRes?.success) {
          setSuccess(`AI Smart Call initiated to ${contact?.name}`);
          // Track usage
          await CommunicationService?.trackUsage('calls', 1);
          
          // Start real-time coaching if enabled
          if (aiSettings?.realTimeCoaching) {
            startRealTimeCoaching();
          }
        } else {
          setError(`Failed to initiate call: ${callRes?.error}`);
        }
      } else {
        setError(`Failed to create call record: ${messageRes?.error}`);
      }
    } catch (error) {
      console.error('Error making smart call:', error);
      setError('Failed to initiate smart call');
    } finally {
      setLoading(false);
    }
  };

  const batchProcessContacts = async () => {
    if (selectedContacts?.length === 0) {
      setError('Please select contacts for batch processing');
      return;
    }

    setAiProcessing(true);
    setError('');

    try {
      const contactsToProcess = contacts?.filter(c => selectedContacts?.includes(c?.id));
      
      const result = await aiSmartDialerService?.batchProcessContacts(
        contactsToProcess,
        aiSettings?.callPurpose
      );

      if (result?.success) {
        setSuccess(`Batch processed ${result?.data?.processed_contacts?.length} contacts with AI insights`);
        
        // Update UI with results
        const processed = result?.data?.processed_contacts;
        processed?.forEach(processedContact => {
          // You could store these insights in local state or database
          console.log(`AI insights for ${processedContact?.contact_name}:`, processedContact);
        });
      } else {
        setError('Batch processing failed');
      }
    } catch (error) {
      console.error('Error in batch processing:', error);
      setError('Batch processing error');
    } finally {
      setAiProcessing(false);
    }
  };

  const startRealTimeCoaching = () => {
    // Simulate real-time coaching - in a real implementation, this would connect to speech recognition
    const mockTranscript = "Hello, this is John from LeadGen Pro. How are you doing today?";
    setConversationTranscript(mockTranscript);
    
    // Get AI coaching
    getRealTimeCoaching(mockTranscript);
  };

  const getRealTimeCoaching = async (transcript) => {
    try {
      const result = await aiSmartDialerService?.analyzeConversation(transcript, {
        purpose: aiSettings?.callPurpose,
        contactName: activeCall?.contactName,
        stage: 'opening'
      });

      if (result?.success) {
        setRealTimeCoaching(result?.data);
      }
    } catch (error) {
      console.error('Error getting real-time coaching:', error);
    }
  };

  const endCall = async () => {
    if (!activeCall) return;

    setLoading(true);
    try {
      // Simulate call ending
      const callData = {
        contactName: activeCall?.contactName,
        duration: Math.floor((new Date() - activeCall?.startTime) / 1000),
        outcome: 'completed',
        notes: 'AI Smart Call completed',
        transcript: conversationTranscript,
        recordingUrl: null // Would be provided by actual call service
      };

      // Get post-call analysis
      if (aiSettings?.postCallAnalysis) {
        const analysisResult = await aiSmartDialerService?.analyzeCallOutcome(callData);
        if (analysisResult?.success) {
          console.log('Post-call analysis:', analysisResult?.data);
          setSuccess(`Call completed. AI Success Rating: ${analysisResult?.data?.success_rating}/10`);
        }
      }

      setActiveCall(null);
      setRealTimeCoaching(null);
      setConversationTranscript('');
      
      // Refresh data
      loadData();
    } catch (error) {
      console.error('Error ending call:', error);
      setError('Error ending call');
    } finally {
      setLoading(false);
    }
  };

  const handleContactSelect = (contactId, checked) => {
    if (checked) {
      setSelectedContacts([...selectedContacts, contactId]);
    } else {
      setSelectedContacts(selectedContacts?.filter(id => id !== contactId));
    }
  };

  const getContactPriority = (contactId) => {
    const priority = prioritizedContacts?.find(p => p?.contact_id === contactId);
    return priority?.priority_score || 50;
  };

  const getContactRecommendation = (contactId) => {
    const priority = prioritizedContacts?.find(p => p?.contact_id === contactId);
    return priority?.suggested_approach || 'Standard approach';
  };

  const callPurposeOptions = [
    { value: 'lead_qualification', label: 'Lead Qualification' },
    { value: 'follow_up', label: 'Follow-up Call' },
    { value: 'demo_booking', label: 'Demo Booking' },
    { value: 'customer_support', label: 'Customer Support' },
    { value: 'survey', label: 'Survey/Feedback' },
    { value: 'appointment', label: 'Appointment Setting' }
  ];

  const toneOptions = [
    { value: 'professional', label: 'Professional' },
    { value: 'friendly', label: 'Friendly' },
    { value: 'consultative', label: 'Consultative' },
    { value: 'casual', label: 'Casual' },
    { value: 'urgent', label: 'Urgent' }
  ];

  if (loadingData) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <Icon name="Loader2" size={24} className="animate-spin mx-auto mb-2 text-primary" />
          <p className="text-muted-foreground">Loading AI Smart Dialer...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* AI Status Banner */}
      {aiMode === 'smart' && (
        <div className="bg-gradient-to-r from-blue-500/10 to-purple-500/10 border border-blue-500/20 rounded-lg p-4">
          <div className="flex items-center space-x-3">
            <Icon name="Brain" size={20} className="text-blue-500" />
            <div className="flex-1">
              <h3 className="text-sm font-semibold text-foreground">AI Smart Dialer Active</h3>
              <p className="text-xs text-muted-foreground">
                Advanced AI is enhancing your calling experience with intelligent scripts, real-time coaching, and analytics
              </p>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setAiMode(aiMode === 'smart' ? 'manual' : 'smart')}
            >
              {aiMode === 'smart' ? 'Disable AI' : 'Enable AI'}
            </Button>
          </div>
        </div>
      )}
      {/* Error/Success Messages */}
      {error && (
        <div className="bg-destructive/10 border border-destructive/20 text-destructive px-4 py-3 rounded-lg flex items-center justify-between">
          <span>{error}</span>
          <Button variant="ghost" size="sm" onClick={() => setError('')}>
            <Icon name="X" size={16} />
          </Button>
        </div>
      )}
      {success && (
        <div className="bg-success/10 border border-success/20 text-success px-4 py-3 rounded-lg flex items-center justify-between">
          <span>{success}</span>
          <Button variant="ghost" size="sm" onClick={() => setSuccess('')}>
            <Icon name="X" size={16} />
          </Button>
        </div>
      )}
      {/* Tab Navigation */}
      <div className="border-b border-border">
        <nav className="-mb-px flex space-x-8">
          {[
            { key: 'dialer', label: 'AI Dialer', icon: 'Phone' },
            { key: 'insights', label: 'AI Insights', icon: 'Brain' },
            { key: 'campaigns', label: 'Campaigns', icon: 'Target' }
          ]?.map((tab) => (
            <button
              key={tab?.key}
              onClick={() => setActiveTab(tab?.key)}
              className={`py-2 px-1 border-b-2 font-medium text-sm flex items-center space-x-2 transition-colors duration-200 ${
                activeTab === tab?.key
                  ? 'border-primary text-primary' :'border-transparent text-muted-foreground hover:text-foreground hover:border-muted-foreground'
              }`}
            >
              <Icon name={tab?.icon} size={16} />
              <span>{tab?.label}</span>
            </button>
          ))}
        </nav>
      </div>
      {/* Active Call Display */}
      {activeCall && (
        <div className="bg-green-500/10 border border-green-500/20 rounded-lg p-4">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center space-x-3">
              <div className="w-3 h-3 bg-green-500 rounded-full animate-pulse"></div>
              <div>
                <h3 className="font-semibold text-foreground">Active Call: {activeCall?.contactName}</h3>
                <p className="text-sm text-muted-foreground">
                  Duration: {Math.floor((new Date() - activeCall?.startTime) / 1000)}s
                </p>
              </div>
            </div>
            <Button variant="destructive" onClick={endCall} disabled={loading}>
              <Icon name="PhoneOff" size={16} className="mr-2" />
              End Call
            </Button>
          </div>

          {/* Real-time Coaching */}
          {realTimeCoaching && (
            <div className="bg-blue-500/10 border border-blue-500/20 rounded-lg p-3 mt-4">
              <h4 className="text-sm font-semibold text-blue-500 mb-2 flex items-center">
                <Icon name="MessageCircle" size={16} className="mr-2" />
                AI Coach Suggestion
              </h4>
              <p className="text-sm text-foreground">{realTimeCoaching?.next_suggestion}</p>
              <div className="flex items-center space-x-4 mt-2 text-xs">
                <span className={`px-2 py-1 rounded-full ${
                  realTimeCoaching?.sentiment === 'positive' ? 'bg-green-100 text-green-700' :
                  realTimeCoaching?.sentiment === 'negative'? 'bg-red-100 text-red-700' : 'bg-yellow-100 text-yellow-700'
                }`}>
                  {realTimeCoaching?.sentiment}
                </span>
                <span className="text-muted-foreground">
                  Engagement: {realTimeCoaching?.engagement_level}
                </span>
              </div>
            </div>
          )}
        </div>
      )}
      {/* Tab Content */}
      {activeTab === 'dialer' && (
        <div className="space-y-6">
          {/* AI Settings */}
          {aiMode === 'smart' && (
            <div className="bg-card border border-border rounded-lg p-6 shadow-elevation-1">
              <h3 className="text-lg font-semibold text-foreground mb-4 flex items-center">
                <Icon name="Settings" size={20} className="mr-2" />
                AI Smart Dialer Settings
              </h3>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div className="space-y-4">
                  <Select
                    label="Call Purpose"
                    placeholder="Select call purpose"
                    options={callPurposeOptions}
                    value={aiSettings?.callPurpose}
                    onChange={(value) => setAiSettings({...aiSettings, callPurpose: value})}
                  />
                  <Select
                    label="Conversation Tone"
                    placeholder="Select tone"
                    options={toneOptions}
                    value={aiSettings?.tone}
                    onChange={(value) => setAiSettings({...aiSettings, tone: value})}
                  />
                </div>
                <div className="space-y-4">
                  <div className="flex items-center space-x-3">
                    <Checkbox
                      checked={aiSettings?.autoScript}
                      onChange={(e) => setAiSettings({...aiSettings, autoScript: e?.target?.checked})}
                    />
                    <label className="text-sm font-medium text-foreground">Auto-generate call scripts</label>
                  </div>
                  <div className="flex items-center space-x-3">
                    <Checkbox
                      checked={aiSettings?.realTimeCoaching}
                      onChange={(e) => setAiSettings({...aiSettings, realTimeCoaching: e?.target?.checked})}
                    />
                    <label className="text-sm font-medium text-foreground">Real-time AI coaching</label>
                  </div>
                  <div className="flex items-center space-x-3">
                    <Checkbox
                      checked={aiSettings?.postCallAnalysis}
                      onChange={(e) => setAiSettings({...aiSettings, postCallAnalysis: e?.target?.checked})}
                    />
                    <label className="text-sm font-medium text-foreground">Post-call AI analysis</label>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* AI Generated Script Preview */}
          {aiGeneratedScript && (
            <div className="bg-card border border-border rounded-lg p-6 shadow-elevation-1">
              <h3 className="text-lg font-semibold text-foreground mb-4 flex items-center">
                <Icon name="FileText" size={20} className="mr-2 text-blue-500" />
                AI-Generated Call Script
              </h3>
              <div className="bg-muted/50 rounded-lg p-4">
                <pre className="whitespace-pre-wrap text-sm text-foreground font-mono">
                  {aiGeneratedScript}
                </pre>
              </div>
            </div>
          )}

          {/* Smart Contact List */}
          <div className="bg-card border border-border rounded-lg shadow-elevation-1">
            <div className="p-4 border-b border-border">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold text-foreground flex items-center">
                  <Icon name="Users" size={20} className="mr-2" />
                  Smart Contact List
                  {aiMode === 'smart' && prioritizedContacts?.length > 0 && (
                    <span className="ml-2 text-sm bg-blue-500/10 text-blue-500 px-2 py-1 rounded-full">
                      AI Prioritized
                    </span>
                  )}
                </h3>
                <div className="flex space-x-2">
                  {aiMode === 'smart' && (
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={() => prioritizeContactsWithAI(contacts)}
                      disabled={aiProcessing || contacts?.length === 0}
                    >
                      <Icon name="Brain" size={16} className="mr-2" />
                      {aiProcessing ? 'Prioritizing...' : 'AI Prioritize'}
                    </Button>
                  )}
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={batchProcessContacts}
                    disabled={selectedContacts?.length === 0 || aiProcessing}
                  >
                    <Icon name="Zap" size={16} className="mr-2" />
                    {aiProcessing ? 'Processing...' : 'Batch Process'}
                  </Button>
                </div>
              </div>
            </div>
            <div className="max-h-96 overflow-y-auto">
              {contacts?.length === 0 ? (
                <div className="p-4 text-center text-muted-foreground">
                  No contacts available. Add contacts first.
                </div>
              ) : (
                contacts?.map((contact) => {
                  const priority = getContactPriority(contact?.id);
                  const recommendation = getContactRecommendation(contact?.id);
                  
                  return (
                    <div
                      key={contact?.id}
                      className={`p-4 border-b border-border last:border-b-0 hover:bg-muted/50 transition-colors duration-200 ${
                        priority >= 80 ? 'border-l-4 border-l-green-500' :
                        priority >= 60 ? 'border-l-4 border-l-yellow-500' :
                        priority >= 40 ? 'border-l-4 border-l-blue-500' : ''
                      }`}
                    >
                      <div className="flex items-center space-x-3">
                        <Checkbox
                          checked={selectedContacts?.includes(contact?.id)}
                          onChange={(e) => handleContactSelect(contact?.id, e?.target?.checked)}
                        />
                        <div className="flex-1">
                          <div className="flex items-center justify-between mb-1">
                            <div>
                              <p className="text-sm font-medium text-foreground">{contact?.name}</p>
                              <p className="text-xs text-muted-foreground">{contact?.phone}</p>
                              <p className="text-xs text-muted-foreground">{contact?.company}</p>
                            </div>
                            {aiMode === 'smart' && priority > 0 && (
                              <div className="text-right">
                                <div className={`text-xs px-2 py-1 rounded-full ${
                                  priority >= 80 ? 'bg-green-100 text-green-700' :
                                  priority >= 60 ? 'bg-yellow-100 text-yellow-700' :
                                  priority >= 40 ? 'bg-blue-100 text-blue-700': 'bg-gray-100 text-gray-700'
                                }`}>
                                  Priority: {Math.round(priority)}
                                </div>
                              </div>
                            )}
                          </div>
                          {aiMode === 'smart' && recommendation !== 'Standard approach' && (
                            <p className="text-xs text-blue-600 bg-blue-50 px-2 py-1 rounded-md">
                              <Icon name="Lightbulb" size={12} className="inline mr-1" />
                              {recommendation}
                            </p>
                          )}
                        </div>
                        <div className="flex space-x-2">
                          <Button
                            variant="outline"
                            size="sm"
                            iconName="MessageSquare"
                            iconSize={14}
                            onClick={() => generateAIScript(contact?.id)}
                            disabled={!contact?.phone || aiProcessing}
                          >
                            AI Script
                          </Button>
                          <Button
                            variant="default"
                            size="sm"
                            iconName="Phone"
                            iconSize={14}
                            onClick={() => initiateSmartCall(contact?.id)}
                            disabled={!contact?.phone || loading || activeCall}
                            className={aiMode === 'smart' ? 'bg-gradient-to-r from-blue-500 to-purple-500 hover:from-blue-600 hover:to-purple-600' : ''}
                          >
                            {aiMode === 'smart' ? 'Smart Call' : 'Call'}
                          </Button>
                        </div>
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </div>
        </div>
      )}
      {activeTab === 'insights' && (
        <div className="space-y-6">
          {/* AI Insights Dashboard */}
          {aiInsights && (
            <div className="bg-card border border-border rounded-lg p-6 shadow-elevation-1">
              <h3 className="text-lg font-semibold text-foreground mb-4 flex items-center">
                <Icon name="TrendingUp" size={20} className="mr-2 text-blue-500" />
                AI Campaign Insights
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="text-center">
                  <div className="text-2xl font-bold text-success">
                    {Math.round(aiInsights?.success_probability * 100)}%
                  </div>
                  <p className="text-sm text-muted-foreground">Success Probability</p>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-primary">
                    {aiInsights?.best_calling_window || 'Business Hours'}
                  </div>
                  <p className="text-sm text-muted-foreground">Best Calling Window</p>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-foreground">
                    {prioritizedContacts?.length || 0}
                  </div>
                  <p className="text-sm text-muted-foreground">Contacts Analyzed</p>
                </div>
              </div>
              <div className="mt-6">
                <h4 className="font-semibold text-foreground mb-3">AI Recommendations</h4>
                <ul className="space-y-2">
                  {aiInsights?.recommendations?.map((rec, index) => (
                    <li key={index} className="flex items-start space-x-2">
                      <Icon name="CheckCircle" size={16} className="text-green-500 mt-0.5" />
                      <span className="text-sm text-foreground">{rec}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          )}

          {/* Top Priority Contacts */}
          {prioritizedContacts?.length > 0 && (
            <div className="bg-card border border-border rounded-lg shadow-elevation-1">
              <div className="p-4 border-b border-border">
                <h3 className="text-lg font-semibold text-foreground">Top Priority Contacts</h3>
              </div>
              <div className="space-y-2 p-4">
                {prioritizedContacts?.slice(0, 5)?.map((priorityContact, index) => {
                  const contact = contacts?.find(c => c?.id === priorityContact?.contact_id);
                  if (!contact) return null;
                  
                  return (
                    <div key={contact?.id} className="flex items-center justify-between p-3 bg-muted/30 rounded-lg">
                      <div className="flex items-center space-x-3">
                        <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${
                          index === 0 ? 'bg-gold text-gold-foreground' :
                          index === 1 ? 'bg-silver text-silver-foreground' :
                          index === 2 ? 'bg-bronze text-bronze-foreground': 'bg-muted text-muted-foreground'
                        }`}>
                          {index + 1}
                        </div>
                        <div>
                          <p className="font-medium text-foreground">{contact?.name}</p>
                          <p className="text-sm text-muted-foreground">{contact?.company}</p>
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="text-sm font-semibold text-foreground">
                          {Math.round(priorityContact?.priority_score)}
                        </div>
                        <p className="text-xs text-muted-foreground">Priority Score</p>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      )}
      {activeTab === 'campaigns' && (
        <div className="space-y-6">
          {/* Call Campaigns */}
          <div className="bg-card border border-border rounded-lg shadow-elevation-1">
            <div className="p-4 border-b border-border">
              <h3 className="text-lg font-semibold text-foreground flex items-center">
                <Icon name="Target" size={20} className="mr-2" />
                Call Campaigns
                {aiMode === 'smart' && (
                  <span className="ml-2 text-sm bg-purple-500/10 text-purple-500 px-2 py-1 rounded-full">
                    AI Enhanced
                  </span>
                )}
              </h3>
            </div>
            <div className="p-4 space-y-4">
              {campaigns?.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  No call campaigns yet
                </div>
              ) : (
                campaigns?.map((campaign) => (
                  <div
                    key={campaign?.id}
                    className="p-4 border border-border rounded-lg hover:bg-muted/30 transition-colors duration-200"
                  >
                    <div className="flex items-center justify-between mb-3">
                      <h4 className="font-medium text-foreground">{campaign?.name}</h4>
                      <div className="flex items-center space-x-2">
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                          campaign?.status === 'sent' ? 'bg-success/10 text-success' : 'bg-primary/10 text-primary'
                        }`}>
                          {campaign?.status}
                        </span>
                        {aiMode === 'smart' && (
                          <span className="px-2 py-1 rounded-full text-xs font-medium bg-purple-500/10 text-purple-500">
                            AI
                          </span>
                        )}
                      </div>
                    </div>
                    
                    <div className="grid grid-cols-3 gap-4 mb-3">
                      <div className="text-center">
                        <div className="text-lg font-semibold text-foreground">{campaign?.sent_count || 0}</div>
                        <div className="text-xs text-muted-foreground">Calls Made</div>
                      </div>
                      <div className="text-center">
                        <div className="text-lg font-semibold text-success">{campaign?.delivered_count || 0}</div>
                        <div className="text-xs text-muted-foreground">Connected</div>
                      </div>
                      <div className="text-center">
                        <div className="text-lg font-semibold text-destructive">{campaign?.failed_count || 0}</div>
                        <div className="text-xs text-muted-foreground">Missed</div>
                      </div>
                    </div>
                    
                    <div className="flex items-center justify-between text-xs text-muted-foreground mb-3">
                      <span>Total: {campaign?.total_contacts || 0}</span>
                      <span>Connect Rate: {campaign?.sent_count ? Math.round((campaign?.delivered_count / campaign?.sent_count) * 100) : 0}%</span>
                    </div>
                    
                    <div className="flex items-center justify-between pt-3 border-t border-border">
                      <span className="text-xs text-muted-foreground">
                        Created: {campaign?.created_at ? new Date(campaign.created_at)?.toLocaleDateString() : 'Unknown'}
                      </span>
                      <div className="flex space-x-2">
                        <Button variant="outline" size="sm" iconName="BarChart3" iconSize={14}>
                          Analytics
                        </Button>
                        {aiMode === 'smart' && (
                          <Button variant="outline" size="sm" iconName="Brain" iconSize={14}>
                            AI Insights
                          </Button>
                        )}
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>

          {/* Recent Call History with AI Analysis */}
          <div className="bg-card border border-border rounded-lg shadow-elevation-1">
            <div className="p-4 border-b border-border">
              <h3 className="text-lg font-semibold text-foreground">Recent Calls</h3>
            </div>
            <div className="max-h-96 overflow-y-auto">
              {callHistory?.length === 0 ? (
                <div className="p-4 text-center text-muted-foreground">
                  No calls yet
                </div>
              ) : (
                callHistory?.map((call) => (
                  <div
                    key={call?.id}
                    className={`p-4 border-b border-border last:border-b-0 ${
                      call?.metadata?.ai_generated ? 'bg-blue-50/50' : ''
                    }`}
                  >
                    <div className="flex items-start space-x-3">
                      <Icon
                        name={call?.status === 'answered' ? 'Phone' : call?.status === 'failed' ? 'PhoneOff' : 'Clock'}
                        size={16}
                        className={
                          call?.status === 'answered' ? 'text-success' :
                          call?.status === 'failed' ? 'text-destructive' : 'text-warning'
                        }
                      />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between mb-1">
                          <span className="text-sm font-medium text-foreground">
                            {call?.contact?.name || 'Unknown Contact'}
                          </span>
                          <div className="flex items-center space-x-2">
                            {call?.metadata?.ai_generated && (
                              <span className="text-xs px-2 py-1 rounded-full bg-blue-100 text-blue-700">
                                AI Call
                              </span>
                            )}
                            <span className="text-xs text-muted-foreground">
                              {call?.created_at ? new Date(call.created_at)?.toLocaleString() : 'Unknown'}
                            </span>
                          </div>
                        </div>
                        <p className="text-xs text-muted-foreground mb-1">
                          {call?.to_address}
                        </p>
                        <div className="flex items-center space-x-2">
                          <span className={`text-xs px-2 py-1 rounded-full ${
                            call?.direction === 'inbound' ? 'bg-primary/10 text-primary' : 'bg-secondary/10 text-secondary'
                          }`}>
                            {call?.direction}
                          </span>
                          <span className={`text-xs ${
                            call?.status === 'answered' ? 'text-success' :
                            call?.status === 'failed' ? 'text-destructive' : 'text-warning'
                          }`}>
                            {call?.status}
                          </span>
                          {call?.metadata?.call_duration && (
                            <span className="text-xs text-muted-foreground">
                              {call?.metadata?.call_duration}s
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AISmartDialerPanel;